import { useInView } from 'react-intersection-observer'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { EmptyObject } from 'lib/object/objectUtils'
import { take } from 'lib/array/arrayUtils'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import CarouselCardLoadingSkeleton from './OfferCards/CarouselCardLoadingSkeleton'
import FullWidthCarouselWithAnalytics from 'components/WithAnalytics/Carousel/FullWidthCarouselWithAnalytics'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import OfferCard from './OfferCards/OfferCard'
import OfferListLoadingOfferCard from './OfferListTiles/OfferListLoadingOfferCard'
import OfferListOfferCard from './OfferCards/OfferListOfferCard'
import useOfferList from 'hooks/Offers/useOfferList'

interface Props {
  /**
   * The set of filters to fetch the offer list with
   */
  filters?: App.OfferListFilters;
  /**
   * Use this to provide your own full offer list for the carousel to display
   */
  overrideOfferList?: App.OfferList;
  /**
   * Number of cards to be shown at max
   * @default 20
   */
  limit?: number;
  /**
   * Temporary limit for mobile to prevent performance issues
   */
  mobileLimit?: number;
  /**
   * The style of card that'll be displayed
   * @default card
   */
  tileStyle?: 'card' | 'hero' | 'search' | 'frontpage';
  /**
   * Pass additional query params to the offer URL generated for each card
   * NOTE: This only works for tilestyle = 'card' at the moment
   */
  additionalOfferUrlQuery?: string;
}

function OfferListCarousel(props: React.PropsWithChildren<Props>) {
  const {
    filters = EmptyObject,
    overrideOfferList,
    limit = 20,
    mobileLimit,
    children,
    tileStyle = 'card',
    additionalOfferUrlQuery,
  } = props

  const filterOfferList = useOfferList(filters, { disabled: !!overrideOfferList || !filters })
  const offerList = overrideOfferList ?? filterOfferList
  const carouselRef = useRef<HTMLDivElement>(null)

  // default to mobile limit, this should still be a reasonable amount
  // to populate the first 'page' of a desktop experience still, especailly as these
  // carousels are usually below the fold
  const [listLimit, setListLimit] = useState<number>(mobileLimit ?? limit)

  const isMobile = useIsMobileScreen()
  useEffect(() => {
    if (isMobile && mobileLimit) {
      setListLimit(mobileLimit)
    } else {
      setListLimit(limit)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  const [inViewRef, carouselInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  useEffect(() => {
    // We've been given a new offer list, send the carousel back to the start
    if (carouselRef.current) {
      carouselRef.current.scrollTo({ left: 0 })
    }
  }, [offerList.key])

  useEffect(() => {
    inViewRef(carouselRef.current)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef.current])

  const offerIds = useMemo(() => take(offerList.offerIds, listLimit), [listLimit, offerList.offerIds])

  return <FullWidthCarouselWithAnalytics
    itemsPerArrow={tileStyle === 'card' ? 3 : 1}
    snap="start"
    ref={carouselRef}
  >
    <LoaderPlayStateContext.Provider value={carouselInView ? 'running' : 'paused'}>
      {tileStyle === 'card' && <>
        {offerList.fetching && <>
          <CarouselCardLoadingSkeleton />
          <CarouselCardLoadingSkeleton />
          <CarouselCardLoadingSkeleton />
          <CarouselCardLoadingSkeleton />
        </>}
        {offerIds.map((id, index) => (
          <OfferCard
            key={id}
            id={id}
            idx={index}
            // eager load the first two, this 'preps' the carousel for mobile view
            lazyLoad={index > 1}
            additionalOfferUrlQuery={additionalOfferUrlQuery}
          />
        ))}
      </>}
      {tileStyle !== 'card' && <>
        {offerList.fetching && <>
          <OfferListLoadingOfferCard tileStyle={tileStyle} />
          <OfferListLoadingOfferCard tileStyle={tileStyle} />
        </>}
        {offerIds.map((id, index) => <OfferListOfferCard
          key={id}
          offerId={id}
          tileStyle={tileStyle}
          position={index}
        />)}
      </>}
      {children}
    </LoaderPlayStateContext.Provider>
  </FullWidthCarouselWithAnalytics>
}

export default React.memo(OfferListCarousel)
